import { Alert, Box, Typography } from '@mui/material';
import { RefreshCw, X } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { createAdminApi, type AdminSyncConflict } from '@/admin/adminApi';
import { formatCompact } from '@/admin/audit/auditPresentation';
import { useAdminQuery } from '@/admin/data/useAdminQuery';
import { hasNextPage } from '@/admin/users/accountPresentation';
import { entityTypeLabel } from '@/admin/users/accountUsagePresentation';
import { useAdminAccess } from '@/admin/users/useAdminAccess';
import { useRefreshOnFocus } from '@/admin/useRefreshOnFocus';
import { useHttpClient } from '@/app/providers';
import {
  AccessDenied,
  Badge,
  Button,
  Card,
  DataTable,
  ErrorState,
  LoadingState,
  MasterDetail,
  PageHeader,
  SelectField,
  useDetailRoute,
  type DataTableColumn,
} from '@/ui/kit';

import { ConflictCounters } from './ConflictCounters';
import { ConflictDetailPanel } from './ConflictDetailPanel';
import {
  CONFLICT_PERIOD_OPTIONS,
  DEFAULT_CONFLICT_FILTERS,
  hasActiveConflictFilter,
  periodPhrase,
  readConflictFilters,
  readConflictPage,
  toConflictQuery,
  writeConflictFilters,
  type ConflictFilters,
  type ConflictPeriod,
} from './conflictFilters';
import {
  CLASSIFICATION_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  STATUS_OPTIONS,
  classificationText,
  describeConflictPage,
  statusLabel,
  statusTone,
} from './conflictPresentation';
import { CONFLICTS_PATH } from './conflictRoutes';

/**
 * Conflits de synchronisation (`/operations/conflicts`, ADR 0039, #635).
 *
 * Le registre des conflits **en lecture**, pour `support` comme pour `admin` :
 * aucun geste ici n'ecrit, tranche ni ne classe (ce sont les actions de #636).
 * Pas de maquette Figma pour cet ecran : il reprend les patrons du journal
 * d'audit (liste filtrable + volet de detail) et de la supervision des
 * synchronisations. Cinq partis pris :
 *
 * 1. **Le filtrage, le total et les compteurs sont ceux du serveur.** Les quatre
 *    compteurs (detectes, ouverts, resolus, sans objet) suivent les filtres
 *    **sauf l'etat**, sans quoi choisir « Ouvert » mettrait les autres a zero.
 * 2. **Les filtres vivent dans l'URL** : ils survivent a l'ouverture du volet, a
 *    un rechargement et a un lien partage — c'est ce qui permet a la fiche d'un
 *    compte d'y renvoyer (`?userId=`).
 * 3. **Toute la periode par defaut** : un conflit ouvert depuis longtemps est
 *    justement ce qu'on vient chercher.
 * 4. **Le contenu refuse n'est jamais montre** (donnee de sante) : ni dans la
 *    liste ni dans le volet, meme conserve.
 * 5. **Pas de rafraichissement automatique** (ADR 0035 §4) : « Actualiser » et la
 *    relecture au retour sur l'onglet, chaque lecture s'ecrivant au journal.
 */

const PER_PAGE = 25;

/** Valeur de l'option « aucun filtre » des listes deroulantes (une valeur vide ne s'affiche pas une fois choisie). */
const ANY = '*';

const toOption = (filter: string): string => (filter === '' ? ANY : filter);

const fromOption = (value: string): string => (value === ANY ? '' : value);

const withAny = (
  anyLabel: string,
  options: readonly { readonly value: string; readonly label: string }[],
  current: string,
  labelOf: (value: string) => string,
): { readonly value: string; readonly label: string }[] => [
  { value: ANY, label: anyLabel },
  ...options,
  // Un filtre venu d'un lien peut etre hors dictionnaire : il reste selectionnable, affiche brut.
  ...(current === '' || options.some((option) => option.value === current) ? [] : [{ value: current, label: labelOf(current) }]),
];

const INVALID_FILTERS_MESSAGE =
  'Le serveur a refusé ces filtres, par exemple un identifiant de compte illisible. Vérifiez-les, puis réessayez.';

const COLUMNS: readonly DataTableColumn<AdminSyncConflict>[] = [
  { key: 'when', header: 'Détecté', render: (conflict) => formatCompact(conflict.detectedAt) ?? '—' },
  {
    key: 'account',
    header: 'Compte',
    primary: true,
    render: (conflict) => (
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{conflict.email}</Box>
        <Typography component="div" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
          {entityTypeLabel(conflict.entityType)}
        </Typography>
      </Box>
    ),
  },
  { key: 'status', header: 'État', render: (conflict) => <Badge label={statusLabel(conflict.status)} tone={statusTone(conflict.status)} /> },
  {
    key: 'classification',
    header: 'Nature',
    render: (conflict) => {
      const text = classificationText(conflict.classification);

      return (
        <Box sx={{ minWidth: 0 }}>
          <Badge label={text.label} tone={text.tone} />
          <Typography component="div" sx={{ fontSize: 12, color: 'tokens.textMuted', mt: 0.5 }}>
            {text.nature}
          </Typography>
        </Box>
      );
    },
  },
  { key: 'count', header: 'Détections', align: 'right', render: (conflict) => conflict.detectionCount },
];

/**
 * Colonnes quand le volet est ouvert : la liste ne garde alors qu'environ la
 * moitie de la largeur. Une seule colonne « Conflit » porte l'essentiel, le
 * volet disant le reste.
 */
const COMPACT_COLUMNS: readonly DataTableColumn<AdminSyncConflict>[] = [
  { key: 'when', header: 'Détecté', render: (conflict) => formatCompact(conflict.detectedAt) ?? '—' },
  {
    key: 'conflict',
    header: 'Conflit',
    primary: true,
    render: (conflict) => (
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{conflict.email}</Box>
        <Typography component="div" sx={{ fontSize: 12, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
          {entityTypeLabel(conflict.entityType)} · {statusLabel(conflict.status)}
        </Typography>
      </Box>
    ),
  },
];

export const ConflictsScreen = (): ReactElement => {
  const http = useHttpClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { access, reload: reloadAccess } = useAdminAccess();
  const granted = access.kind === 'granted';

  const filters = useMemo(() => readConflictFilters(searchParams), [searchParams]);
  const page = readConflictPage(searchParams);
  const detail = useDetailRoute(CONFLICTS_PATH);

  const [token, setToken] = useState(0);
  const refresh = useCallback((): void => {
    setToken((previous) => previous + 1);
  }, []);

  useRefreshOnFocus(refresh);

  const queryKey = `conflicts:${writeConflictFilters(filters, page).toString()}`;
  const list = useAdminQuery(
    queryKey,
    // « Maintenant » est lu ici, au chargement : une periode relative glisse avec chaque actualisation.
    () => createAdminApi(http).listSyncConflicts(toConflictQuery(filters, new Date()), page, PER_PAGE),
    { enabled: granted, token },
  );

  /** Change les filtres ou la page ; le volet se ferme, le conflit ouvert pouvant sortir de la liste. */
  const go = useCallback(
    (next: ConflictFilters, nextPage = 1): void => {
      void navigate({ pathname: CONFLICTS_PATH, search: writeConflictFilters(next, nextPage).toString() });
    },
    [navigate],
  );

  const patch = (change: Partial<ConflictFilters>): void => {
    go({ ...filters, ...change });
  };

  const result = list.data;
  const items = result?.items ?? [];
  const invalidFilters = list.failure?.invalid === true;
  const pageState = {
    total: result?.total ?? 0,
    page: result?.page ?? page,
    perPage: result?.perPage ?? PER_PAGE,
    itemCount: items.length,
  };

  // Quand un compte est filtre, toute ligne rendue le porte : la premiere dit comment le nommer.
  const accountChip = filters.userId === '' ? null : (items[0]?.email ?? 'compte choisi');

  const renderBody = (): ReactElement => {
    if (access.kind === 'loading') {
      return <LoadingState label="Vérification de votre accès à l’administration…" />;
    }

    if (access.kind === 'error') {
      return <ErrorState message={access.message} onRetry={reloadAccess} />;
    }

    if (access.kind === 'denied') {
      return (
        <AccessDenied title="Votre compte n’a pas accès à l’administration">
          Vous êtes bien connecté, mais ce compte n’a aucun rôle d’administration : il ne peut donc pas consulter les
          conflits de synchronisation. Un rôle s’attribue depuis le serveur, jamais depuis cet écran.
        </AccessDenied>
      );
    }

    const listPane = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {result === null ? null : <ConflictCounters counts={result.counts} period={periodPhrase(filters.period)} />}

        <Card title="Filtres" headingLevel="h2">
          <Box component="search" aria-label="Filtres des conflits" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              <SelectField
                label="Période de détection"
                value={filters.period}
                options={CONFLICT_PERIOD_OPTIONS}
                onChange={(value) => {
                  patch({ period: value as ConflictPeriod });
                }}
              />
              <SelectField
                label="État"
                value={toOption(filters.status)}
                options={withAny('Tous les états', STATUS_OPTIONS, filters.status, statusLabel)}
                onChange={(value) => {
                  patch({ status: fromOption(value) });
                }}
              />
              <SelectField
                label="Nature du conflit"
                value={toOption(filters.classification)}
                options={withAny('Toutes les natures', CLASSIFICATION_OPTIONS, filters.classification, (value) => classificationText(value).label)}
                onChange={(value) => {
                  patch({ classification: fromOption(value) });
                }}
              />
              <SelectField
                label="Type d’enregistrement"
                value={toOption(filters.entityType)}
                options={withAny('Tous les enregistrements', ENTITY_TYPE_OPTIONS, filters.entityType, entityTypeLabel)}
                onChange={(value) => {
                  patch({ entityType: fromOption(value) });
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
              {accountChip === null ? null : (
                <Button
                  size="S"
                  icon={X}
                  aria-label={`Retirer le filtre « Compte : ${accountChip} »`}
                  onClick={() => {
                    patch({ userId: '' });
                  }}
                >
                  {`Compte : ${accountChip}`}
                </Button>
              )}
              {hasActiveConflictFilter(filters) ? (
                <Button
                  size="S"
                  variant="ghost"
                  onClick={() => {
                    go(DEFAULT_CONFLICT_FILTERS);
                  }}
                >
                  Réinitialiser les filtres
                </Button>
              ) : null}
            </Box>
          </Box>
        </Card>

        {list.failure === null ? null : list.failure.forbidden ? (
          <AccessDenied title="Votre rôle ne permet pas de consulter les conflits">
            Votre compte reste connecté : il manque le rôle d’administration requis.
          </AccessDenied>
        ) : invalidFilters ? (
          <ErrorState
            title="Filtres invalides"
            message={INVALID_FILTERS_MESSAGE}
            retryLabel="Réinitialiser les filtres"
            onRetry={() => {
              go(DEFAULT_CONFLICT_FILTERS);
            }}
          />
        ) : (
          <ErrorState message={list.failure.message} onRetry={refresh} />
        )}

        {/* La liste d'une demande refusee n'est pas montree : elle se lirait comme le resultat des filtres. */}
        {invalidFilters || list.failure?.forbidden === true ? null : (
          <Card>
            {result === null && list.busy ? <LoadingState label="Chargement des conflits…" /> : null}
            {result === null ? null : (
              <>
                <DataTable<AdminSyncConflict>
                  label="Conflits de synchronisation"
                  columns={detail.selectedId === null ? COLUMNS : COMPACT_COLUMNS}
                  rows={items}
                  getRowId={(conflict) => conflict.id}
                  onRowSelect={(conflict) => {
                    detail.open(conflict.id);
                  }}
                  selectedRowId={detail.selectedId}
                  emptyMessage={
                    pageState.total > 0
                      ? 'Cette page est au-delà de la dernière : revenez en arrière pour retrouver des conflits.'
                      : hasActiveConflictFilter(filters)
                        ? 'Aucun conflit ne correspond à ces filtres.'
                        : 'Aucun conflit enregistré.'
                  }
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 2 }}>
                  <Button
                    onClick={() => {
                      go(filters, Math.max(1, pageState.page - 1));
                    }}
                    disabled={pageState.page <= 1 || list.busy}
                  >
                    Page précédente
                  </Button>
                  <Typography sx={{ fontSize: 14, color: 'tokens.textMuted', textAlign: 'center' }}>{describeConflictPage(pageState)}</Typography>
                  <Button
                    onClick={() => {
                      go(filters, pageState.page + 1);
                    }}
                    disabled={!hasNextPage(pageState) || list.busy}
                  >
                    Page suivante
                  </Button>
                </Box>
              </>
            )}
          </Card>
        )}
      </Box>
    );

    return (
      <MasterDetail
        list={listPane}
        selectedId={detail.selectedId}
        detailLabel="Détail du conflit"
        onClose={detail.close}
        renderDetail={(id) => (
          <ConflictDetailPanel
            conflictId={id}
            token={token}
            onRetry={refresh}
            onFilterAccount={(userId) => {
              patch({ userId });
            }}
          />
        )}
      />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        eyebrow="Exploitation"
        title="Conflits"
        subtitle="Les conflits de synchronisation enregistrés par le serveur, un par saisie refusée. En lecture seule : cet écran ne montre jamais le contenu refusé."
        actions={
          granted ? (
            <Button icon={RefreshCw} onClick={refresh} disabled={list.busy}>
              Actualiser
            </Button>
          ) : undefined
        }
      />
      <Alert severity="info" role="note">
        Les conflits antérieurs à la mise en service du registre n’apparaissent qu’après la reprise de l’historique
        (n° 634). La liste filtre la période sur la date de détection ; les compteurs, eux, ont chacun leur date.
      </Alert>
      {renderBody()}
    </Box>
  );
};
